/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useServiceContext } from '../../root'

const ReadOnlyValueStyledDiv = styled.div<{ disabled?: boolean; inherited?: boolean }>`
  color: ${(props) => (props.disabled ? '#B7B7B7' : '#000')};
  font-style: ${(props) => (props.inherited ? 'italic' : 'normal')};
  border-radius: 0;
  box-shadow: none;
  border: 1px solid #ccc;
  min-height: 33px;
  font-size: 14px;
  padding: 3px 0px 3px 10px;

  span {
    display: block;
  }
`

interface DatePickerWrapperProps {
  input: React.ReactNode
  value?: () => string | null
  formatter?: (val: any) => string
  property?: { value: any }
  alwaysEditable?: boolean // If true, stays in edit mode always (useful for filters)
}

export const DatePickerWrapper: React.FC<DatePickerWrapperProps> = ({
  input,
  value,
  formatter,
  property,
  alwaysEditable = false
}) => {
  const context = useServiceContext()
  const {
    service: {
      filter: { translate }
    }
  } = context
  const [inEditMode, setInEditMode] = useState(true)
  const [displayValue, setDisplayValue] = useState<string | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Skip click-outside handler if always editable
    if (alwaysEditable) return

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        // Don't disable if clicking on another date picker or its calendar
        const target = event.target as HTMLElement
        const isDatePickerElement =
          target.closest('.react-datepicker') ||
          target.closest('.react-datepicker-popper') ||
          target.closest('.react-datepicker__input-container') ||
          target.classList.contains('react-datepicker__navigation') ||
          target.classList.contains('react-datepicker__day') ||
          target.classList.contains('react-datepicker__time-list-item')

        if (!isDatePickerElement) {
          setInEditMode(false)
        }
      }
    }

    if (inEditMode) {
      document.addEventListener('click', handleClickOutside)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [inEditMode, alwaysEditable])

  useEffect(() => {
    formatValue()
  }, [value])

  function formatValue() {
    const val = typeof value === 'function' ? value() : property?.value

    setDisplayValue(
      typeof formatter === 'undefined'
        ? Array.isArray(val) && val.length > 0
          ? val.reduce((mem: string, item: any, index: number) => {
              return mem + item + (index < val.length - 1 ? ', ' : '')
            }, '')
          : val === null || (typeof val === 'string' && val.length === 0)
            ? translate('-')
            : val
        : formatter(val)
    )
  }

  return (
    <div title={displayValue || ''} ref={wrapperRef}>
      {inEditMode ? (
        <div>{input}</div>
      ) : (
        <ReadOnlyValueStyledDiv>
          <span
            onClick={() => {
              setInEditMode(true)
            }}
          >
            {displayValue}
          </span>
        </ReadOnlyValueStyledDiv>
      )}
    </div>
  )
}

export default DatePickerWrapper
